import { useState } from 'react'
import { useParams } from 'react-router'

import { PageBody, PageHeader } from '@/components/AppShell'
import { ErrorState, TableSkeleton } from '@/components/states'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatAbsolute } from '@/lib/format'

import { runAction, useStaffMutation, useStaffUser, type StaffAction } from '../api'
import { useStaff } from '../hooks'
import { ActionNotice, Fact, ReasonDialog, StaffGate, StaffLink, StateBadge } from '../ui'

type Dialog = 'suspend' | 'reactivate' | 'verify'

export function StaffUserPage() {
  const { userId } = useParams()
  const staff = useStaff()
  const user = useStaffUser(userId)
  const [dialog, setDialog] = useState<Dialog | null>(null)
  const [notice, setNotice] = useState<{ title: string; notified: boolean | undefined } | null>(null)
  const act = useStaffMutation(({ action, reason }: { action: StaffAction; reason: string }) => runAction(action, reason))
  const data = user.data
  const canOperate = staff.can('suspend_reactivate')
  const suspended = data?.user_state !== 'ACTIVE'

  return (
    <StaffGate>
      <PageHeader
        eyebrow="Staff · User"
        title={data?.email ?? 'User'}
        description={data === undefined ? undefined : <span className="font-mono text-xs">{data.id}</span>}
        actions={
          data === undefined ? undefined : (
            <div className="flex flex-wrap justify-end gap-2">
              {staff.can('deletion_cancel_verify') && !data.email_verified ? (
                <Button variant="outline" onClick={() => { setDialog('verify') }}>Resend verification</Button>
              ) : null}
              {canOperate ? (
                suspended ? (
                  <Button onClick={() => { setDialog('reactivate') }}>Reactivate</Button>
                ) : (
                  <Button variant="destructive" onClick={() => { setDialog('suspend') }}>Suspend</Button>
                )
              ) : null}
            </div>
          )
        }
      />
      <PageBody>
        <ActionNotice notice={notice} onDismiss={() => { setNotice(null) }} />
        {user.isError ? (
          <ErrorState error={user.error} title="Could not load this user" onRetry={() => void user.refetch()} />
        ) : data === undefined ? (
          <TableSkeleton rows={3} columns={3} />
        ) : (
          <>
            <Card>
              <CardContent>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
                  <Fact label="Name">{data.name ?? '—'}</Fact>
                  <Fact label="State"><StateBadge state={data.user_state} /></Fact>
                  <Fact label="Email verified">{data.email_verified ? 'yes' : 'no'}</Fact>
                  <Fact label="Connection token">{data.has_connection_token ? 'issued' : 'none'}</Fact>
                  <Fact label="Created">{formatAbsolute(data.created_at)}</Fact>
                  <Fact label="State changed">{data.state_changed_at === null ? '—' : formatAbsolute(data.state_changed_at)}</Fact>
                </dl>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Accounts</CardTitle></CardHeader>
              <CardContent className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Account</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>State</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.memberships.map((m) => (
                      <TableRow key={m.account_id} data-testid="staff-membership-row">
                        <TableCell className="max-w-[18rem] truncate"><StaffLink to={`/app/staff/accounts/${m.account_id}`}>{m.account_name}</StaffLink></TableCell>
                        <TableCell><Badge variant="secondary">{m.role}</Badge></TableCell>
                        <TableCell><StateBadge state={m.account_state} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        )}
      </PageBody>

      {data === undefined || dialog === null ? null : (
        <ReasonDialog
          title={
            dialog === 'suspend' ? `Suspend ${data.email}?` : dialog === 'reactivate' ? `Reactivate ${data.email}?` : 'Resend verification email?'
          }
          description={
            dialog === 'suspend'
              ? 'They can no longer sign in to the console or connect agents. The last administrator of an account cannot be suspended. Their accounts’ administrators are emailed with your reason.'
              : dialog === 'reactivate'
                ? 'They can sign in and connect again. Their accounts’ administrators are emailed with your reason.'
                : 'Sends the verification link again. The user is the only recipient.'
          }
          confirmLabel={dialog === 'suspend' ? 'Suspend user' : dialog === 'reactivate' ? 'Reactivate user' : 'Send email'}
          destructive={dialog === 'suspend'}
          onClose={() => { setDialog(null) }}
          onConfirm={async (reason) => {
            const kind = dialog === 'suspend' ? 'user-suspend' : dialog === 'reactivate' ? 'user-reactivate' : 'user-resend-verification'
            const r = await act.mutateAsync({ action: { kind, id: data.id }, reason })
            setNotice({
              title: dialog === 'suspend' ? 'User suspended' : dialog === 'reactivate' ? 'User reactivated' : 'Verification email sent',
              notified: dialog === 'verify' ? undefined : r.notified,
            })
          }}
        />
      )}
    </StaffGate>
  )
}
